import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import { selectAppName, selectAppSettings, selectReleasePilotEligibleUsersForApp } from '../../selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { AppComponentProps } from '../../typescriptHelpers';
import Select from 'antd/lib/select';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface AddReleasePilotsModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const AddReleasePilotsModal: React.FC<AddReleasePilotsModalProps> = (props: AddReleasePilotsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const addPilotsIsLoading = useIsLoading(AppSettingsUpdatable.UpdateReleasePilotList);
    const addPilotsError = useError(AppSettingsUpdatable.UpdateReleasePilotList);
    const addPilotsSuccess = useUpdated(AppSettingsUpdatable.UpdateReleasePilotList);

    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const eligiblePilotUsers = useSelector((state: RootState) =>
        selectReleasePilotEligibleUsersForApp(state, props.appId),
    );

    const existingReleasePilotUsers = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings.releasePilotIds;
    });

    const addReleasePilotUsers = (values) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateReleasePilotList, {
                orgId: props.orgId,
                appId: props.appId,
                releasePilotIds: values.pilotUsers,
            }),
        );
    };

    React.useEffect(() => {
        if (addPilotsError) {
            globalMessage.error(addPilotsError);
        }
    }, [addPilotsError]);

    React.useEffect(() => {
        if (addPilotsSuccess) {
            globalMessage.success('Release pilot list updated!');

            props.onCancel();
        }
    }, [addPilotsSuccess]);

    return (
        <Modal
            title="Add users to the rotation"
            okButtonProps={{ loading: addPilotsIsLoading }}
            onOk={() => form.submit()}
            okText="Add"
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                form.resetFields();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>
                    Add app users to the release pilot rotation for {appName}. App users must belong to a group that is
                    eligible for release pilot rotation.
                </Text>
                <Form
                    onFinish={addReleasePilotUsers}
                    initialValues={{ pilotUsers: [] }}
                    style={{ marginTop: 24 }}
                    form={form}
                >
                    <Form.Item
                        name="pilotUsers"
                        rules={[
                            {
                                required: true,
                                message: 'At least one release pilot user is required.',
                            },
                        ]}
                        hasFeedback
                    >
                        <Select placeholder={'Select'} autoFocus mode="multiple">
                            {eligiblePilotUsers.map((user) => {
                                const isAlreadyPilot = existingReleasePilotUsers.includes(user.id);
                                return (
                                    <Select.Option key={user.email} value={user.id} disabled={isAlreadyPilot}>
                                        {user.email}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default AddReleasePilotsModal;
